import { useState } from 'react'
import * as zipcodes from 'zipcodes'
import type { Clinic } from '../types'
import type { ClinicFields } from '../services/clinics'
import { formatClinicAddress } from '../services/clinics'
import { formatTin } from '../utils/formatters'
import { isValidPhone, isValidEmail } from '../utils/validators'
import PhoneInput from './PhoneInput'
import EmailInput from './EmailInput'
import { isValidNpi } from '../utils/npi'
import { WEEK, parseOperatingDays, type DayKey } from '../../shared/operatingDays'

interface ClinicsListProps {
  clinics: Clinic[]
  onAddClinic: (fields: ClinicFields) => Promise<void>
  onUpdateClinic: (id: string, fields: ClinicFields) => Promise<void>
  onArchiveClinic: (id: string) => Promise<void>
}

type SortCol = 'name' | 'address' | 'phone'
type SortDir = 'asc' | 'desc'

function ColTh({
  label, col, sortBy, sortDir, onSort, disabled,
}: {
  label: string; col: SortCol; sortBy: SortCol; sortDir: SortDir; onSort: (c: SortCol) => void; disabled?: boolean
}) {
  const active = !disabled && sortBy === col
  return (
    <th
      className={`clinics-table__th${!disabled ? ' sortable-th' : ''}${active ? ' sortable-th--active' : ''}`}
      onClick={disabled ? undefined : () => onSort(col)}
    >
      {label}
      {active && <span className="sort-arrow">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  )
}

const emptyForm: ClinicFields = {
  name: '', street: '', city: '', state: '', zip: '',
  phone: '', tin: '', legalName: '', groupNpi: '', email: '',
  // All days open by default — matches pre-#133 behavior; closing a day is a
  // deliberate admin action, never an onboarding default.
  operatingDays: [...WEEK],
}

/** Single letters for the day pills, Sun-first to match WEEK order. */
const DAY_INITIAL: Record<DayKey, string> = {
  sun: 'S', mon: 'M', tue: 'T', wed: 'W', thu: 'T', fri: 'F', sat: 'S',
}

const isValidTin = (v: string) => /^\d{2}-\d{7}$/.test(v)
const isValidZip = (v: string) => /^\d{5}$/.test(v)
// Group NPI is optional (only Medicare-serving clinics need one) — empty is
// allowed, but a non-empty value must be a well-formed 10-digit NPI.
const isValidGroupNpi = (v: string) => v === '' || isValidNpi(v)

/** Pre-fill form from a clinic doc — handles both legacy (address) and new (street/city/state/zip) docs. */
function clinicToForm(clinic: Clinic): ClinicFields {
  return {
    name: clinic.name,
    street: clinic.street ?? clinic.address ?? '',
    city: clinic.city ?? '',
    state: clinic.state ?? '',
    zip: clinic.zip ?? '',
    phone: clinic.phone ?? '',
    tin: clinic.tin ?? '',
    legalName: clinic.legalName ?? '',
    groupNpi: clinic.groupNpi ?? '',
    email: clinic.email ?? '',
    operatingDays: parseOperatingDays(clinic.operatingDays) ?? [...WEEK],
  }
}

export default function ClinicsList({ clinics, onAddClinic, onUpdateClinic, onArchiveClinic }: ClinicsListProps) {
  const [sortBy, setSortBy] = useState<SortCol>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  function handleSort(col: SortCol) {
    if (col === sortBy) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  const sortedClinics = [...clinics].sort((a, b) => {
    const mult = sortDir === 'asc' ? 1 : -1
    let va = '', vb = ''
    if (sortBy === 'name')    { va = a.name; vb = b.name }
    if (sortBy === 'address') { va = formatClinicAddress(a); vb = formatClinicAddress(b) }
    if (sortBy === 'phone')   { va = a.phone ?? ''; vb = b.phone ?? '' }
    return mult * va.localeCompare(vb, undefined, { sensitivity: 'base' })
  })

  const [form, setForm] = useState<ClinicFields>(emptyForm)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  function openCreate() {
    setForm(emptyForm)
    setAttempted(false)
    setSaveError(null)
    setEditingId(null)
    setShowModal(true)
  }

  function openEdit(clinic: Clinic) {
    setForm(clinicToForm(clinic))
    setAttempted(false)
    setSaveError(null)
    setEditingId(clinic.id)
    setShowModal(true)
  }

  function closeModal() {
    setForm(emptyForm)
    setAttempted(false)
    setSaveError(null)
    setEditingId(null)
    setShowModal(false)
  }

  function handleZipChange(raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, 5)
    setForm(f => {
      const updated = { ...f, zip: digits }
      if (digits.length === 5) {
        const info = zipcodes.lookup(digits)
        if (info) {
          updated.city = info.city
          updated.state = info.state
        }
      }
      return updated
    })
  }

  async function handleSubmit() {
    setAttempted(true)
    if (!canSubmit) return
    setSaving(true)
    setSaveError(null)
    try {
      if (editingId) {
        await onUpdateClinic(editingId, form)
      } else {
        await onAddClinic(form)
      }
      closeModal()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed. Check your permissions and try again.')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive(id: string) {
    setSaving(true)
    try {
      await onArchiveClinic(id)
      closeModal()
    } finally {
      setSaving(false)
    }
  }

  const errors = {
    name:      form.name.trim().length === 0   ? true : false,
    street:    form.street.trim().length === 0  ? true : false,
    city:      form.city.trim().length === 0    ? true : false,
    state:     form.state.trim().length === 0   ? true : false,
    zip:       !isValidZip(form.zip),
    phone:     !isValidPhone(form.phone),
    tin:       !isValidTin(form.tin),
    legalName: form.legalName.trim().length === 0,
    groupNpi:  !isValidGroupNpi(form.groupNpi),
    email:     !isValidEmail(form.email),
    operatingDays: form.operatingDays.length === 0,
  }
  const canSubmit = !Object.values(errors).some(Boolean)

  function inv(field: keyof typeof errors) {
    return attempted && errors[field] ? ' is-invalid' : ''
  }

  return (
    <>
      <div className="clinics-view">
        <div className="clinics-view__header">
          <div className="clinics-view__title-row">
            <svg className="clinics-view__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M8 11h2m4 0h2M8 15h2m4 0h2" />
            </svg>
            <h2 className="clinics-view__title">Clinics</h2>
          </div>
          <button className="create-doctor-btn create-doctor-btn--primary" onClick={openCreate}>
            + Create Clinic
          </button>
        </div>

        <div className="clinics-table-wrap">
          {clinics.length === 0 ? (
            <p className="clinics-view__empty">No clinics yet. Add one to get started.</p>
          ) : (
            <table className="clinics-table">
              <colgroup>
                <col style={{ width: '30%' }} />
                <col style={{ width: '50%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr className="clinics-table__head-row">
                  <ColTh label="Clinic Name" col="name"    sortBy={sortBy} sortDir={sortDir} onSort={handleSort} disabled={sortedClinics.length <= 1} />
                  <ColTh label="Address"     col="address" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} disabled={sortedClinics.length <= 1} />
                  <ColTh label="Phone"       col="phone"   sortBy={sortBy} sortDir={sortDir} onSort={handleSort} disabled={sortedClinics.length <= 1} />
                </tr>
              </thead>
              <tbody>
                {sortedClinics.map(clinic => {
                  const addr = formatClinicAddress(clinic)
                  return (
                    <tr
                      key={clinic.id}
                      className="clinics-table__row"
                      onClick={() => openEdit(clinic)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(clinic) } }}
                    >
                      <td className="clinics-table__cell clinics-table__cell--name">
                        {clinic.name}
                      </td>
                      <td className="clinics-table__cell">
                        {addr ? (
                          <span className="clinics-table__meta">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="clinics-table__meta-icon">
                              <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z" />
                              <circle cx="12" cy="10" r="3" />
                            </svg>
                            {addr}
                          </span>
                        ) : (
                          <span className="clinics-table__empty-cell">—</span>
                        )}
                      </td>
                      <td className="clinics-table__cell">
                        {clinic.phone ? (
                          <span className="clinics-table__meta">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="clinics-table__meta-icon">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.59 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.06 6.06l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            {clinic.phone}
                          </span>
                        ) : (
                          <span className="clinics-table__empty-cell">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="doctor-modal-overlay" onClick={closeModal}>
          <div className="doctor-modal" onClick={e => e.stopPropagation()}>
            <div className="doctor-modal__header">
              <h3 className="doctor-modal__title">{editingId ? 'Edit Clinic' : 'Add Clinic'}</h3>
              <button className="doctor-modal__close" onClick={closeModal} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="add-doctor-form">
              <div className="add-doctor-form__fields">

                <div className="form-field form-field--full">
                  <label className="form-field__label">Clinic Name *</label>
                  <input
                    className={`form-field__input${inv('name')}`}
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="VitalRev Primary Care"
                    autoFocus
                  />
                </div>

                <div className="form-field form-field--full">
                  <label className="form-field__label">Legal Business Name *</label>
                  <input
                    className={`form-field__input${inv('legalName')}`}
                    value={form.legalName}
                    onChange={e => setForm(f => ({ ...f, legalName: e.target.value }))}
                    placeholder="VitalRev Primary Care LLC"
                  />
                </div>

                <div className="form-field form-field--full">
                  <label className="form-field__label">Address *</label>
                  <input
                    className={`form-field__input${inv('street')}`}
                    value={form.street}
                    onChange={e => setForm(f => ({ ...f, street: e.target.value }))}
                    placeholder="3400 Medical Pkwy"
                  />
                </div>

                <div className="form-field-row">
                  <div className="form-field form-field--zip">
                    <label className="form-field__label">ZIP *</label>
                    <input
                      className={`form-field__input${inv('zip')}`}
                      value={form.zip}
                      onChange={e => handleZipChange(e.target.value)}
                      placeholder="78756"
                      inputMode="numeric"
                      maxLength={5}
                    />
                  </div>
                  <div className="form-field form-field--city">
                    <label className="form-field__label">City *</label>
                    <input
                      className={`form-field__input${inv('city')}`}
                      value={form.city}
                      onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                      placeholder="Austin"
                    />
                  </div>
                  <div className="form-field form-field--state">
                    <label className="form-field__label">State *</label>
                    <input
                      className={`form-field__input${inv('state')}`}
                      value={form.state}
                      onChange={e => setForm(f => ({ ...f, state: e.target.value.toUpperCase().slice(0, 2) }))}
                      placeholder="TX"
                      maxLength={2}
                    />
                  </div>
                </div>

                <div className="form-field form-field--full">
                  <label className="form-field__label">Phone *</label>
                  <PhoneInput
                    className={`form-field__input${inv('phone')}`}
                    value={form.phone}
                    onValueChange={val => setForm(f => ({ ...f, phone: val }))}
                    placeholder="(512) 555-0200"
                  />
                </div>

                <div className="form-field form-field--full">
                  <label className="form-field__label">Operating Days *</label>
                  <div className={`day-pills${attempted && errors.operatingDays ? ' day-pills--invalid' : ''}`} role="group" aria-label="Days the clinic is open">
                    {WEEK.map((day) => {
                      const on = form.operatingDays.includes(day)
                      return (
                        <button
                          key={day}
                          type="button"
                          className={`day-pill${on ? ' day-pill--on' : ''}`}
                          aria-pressed={on}
                          title={day.charAt(0).toUpperCase() + day.slice(1)}
                          onClick={() => setForm(f => ({
                            ...f,
                            operatingDays: f.operatingDays.includes(day)
                              ? f.operatingDays.filter(d => d !== day)
                              : WEEK.filter(d => f.operatingDays.includes(d) || d === day),
                          }))}
                        >
                          {DAY_INITIAL[day]}
                        </button>
                      )
                    })}
                  </div>
                  <div className="day-pills__hint">
                    {attempted && errors.operatingDays
                      ? 'At least one open day is required.'
                      : 'Closed days won’t expect appointments or flag sync issues.'}
                  </div>
                </div>

                <div className="form-field form-field--full">
                  <label className="form-field__label">TIN (Tax ID) *</label>
                  <input
                    className={`form-field__input${inv('tin')}`}
                    value={form.tin}
                    onChange={e => setForm(f => ({ ...f, tin: formatTin(e.target.value) }))}
                    placeholder="12-3456789"
                    inputMode="numeric"
                  />
                </div>

                <div className="form-field form-field--full">
                  <label className="form-field__label">Group NPI (Medicare)</label>
                  <input
                    className={`form-field__input${inv('groupNpi')}`}
                    value={form.groupNpi}
                    onChange={e => setForm(f => ({ ...f, groupNpi: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    placeholder="1234567890 — required for Medicare patients"
                    inputMode="numeric"
                    maxLength={10}
                  />
                </div>

                <div className="form-field form-field--full">
                  <label className="form-field__label">Email *</label>
                  <EmailInput
                    className={`form-field__input${inv('email')}`}
                    value={form.email}
                    onValueChange={val => setForm(f => ({ ...f, email: val }))}
                    placeholder="admin@vitalrevcardiology.com"
                  />
                </div>

              </div>
              {saveError && <p className="clinic-save-error">{saveError}</p>}
              <div className="add-doctor-form__actions">
                {editingId ? (
                  <button
                    className="add-doctor-form__delete"
                    onClick={() => handleArchive(editingId)}
                    disabled={saving}
                  >
                    Archive
                  </button>
                ) : (
                  <span />
                )}
                <div className="add-doctor-form__actions-right">
                  <button className="add-doctor-form__cancel" onClick={closeModal}>Cancel</button>
                  <button
                    className="check-btn add-doctor-form__submit"
                    disabled={saving}
                    onClick={handleSubmit}
                  >
                    {editingId ? 'Save Changes' : 'Add Clinic'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
